import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge, StatusDot } from "@/components/Status";
import { Building2, HardDrive, DoorClosed, Cable, ArrowUpRight, Phone } from "lucide-react";

function ratioPercent(ratio) {
  if (!ratio || !ratio.total || ratio.total === 0) return null;
  return Math.round((ratio.online / ratio.total) * 100);
}

function ratioColor(pct) {
  if (pct >= 80) return "emerald";
  if (pct >= 50) return "amber";
  return "red";
}

const VALUE_COLOR = {
  emerald: "text-emerald-600",
  amber: "text-amber-500",
  red: "text-red-600",
};

const BAR_COLOR = {
  emerald: "bg-emerald-500",
  amber: "bg-amber-400",
  red: "bg-red-500",
};

const ACCENT_COLOR = {
  emerald: "border-l-emerald-500",
  amber:   "border-l-amber-400",
  red:     "border-l-red-500",
};

function Metric({ icon: Icon, label, value, sub, ratio, alert }) {
  const pct = ratioPercent(ratio);
  const color = pct !== null ? ratioColor(pct) : null;

  return (
    <div className={`relative rounded-sm border border-zinc-200 bg-white p-5 border-l-2 ${color ? ACCENT_COLOR[color] : "border-l-zinc-300"}`}>
      {alert && pct !== null && pct < 80 && (
        <span className="status-ping absolute right-3 top-3" />
      )}
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-400">
          {label}
        </span>
        <Icon className="h-3.5 w-3.5 text-zinc-300" />
      </div>
      <div
        className={`mt-2 font-head text-4xl font-black tracking-tighter ${
          color ? VALUE_COLOR[color] : "text-zinc-900"
        }`}
      >
        {value}
      </div>
      {sub && <div className="mt-0.5 font-mono text-[11px] text-zinc-400">{sub}</div>}
      {pct !== null && (
        <div className="mt-4 space-y-1">
          <div className="flex items-center justify-between font-mono text-[9px] text-zinc-400">
            <span>{ratio.online} / {ratio.total}</span>
            <span className={color ? VALUE_COLOR[color] : "text-zinc-500"}>{pct}%</span>
          </div>
          <div className="h-[3px] rounded-full bg-zinc-100">
            <div
              className={`h-full rounded-full transition-all duration-700 ${BAR_COLOR[color]}`}
              style={{ width: `${pct}%` }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [hotels, setHotels] = useState([]);

  useEffect(() => {
    api.get("/stats").then((r) => setStats(r.data));
    api.get("/hotels").then((r) => setHotels(r.data));
  }, []);

  const isSuper = user?.role === "super_admin";

  return (
    <div data-testid="dashboard-page">
      <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">
        {isSuper ? "Vue globale" : "Vue hôtel"}
      </p>
      <h1 className="mt-1 font-head text-3xl font-black tracking-tighter text-zinc-900">
        Tableau de bord
      </h1>

      <div className="mt-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Metric
          icon={Building2}
          label="Hôtels"
          value={stats?.hotels ?? "—"}
          sub="tenants actifs"
        />
        {isSuper && (
          <Metric
            icon={HardDrive}
            label="Serveurs SIP"
            value={stats ? `${stats.servers_online}/${stats.servers}` : "—"}
            sub="en ligne"
            ratio={stats ? { online: stats.servers_online, total: stats.servers } : null}
            alert
          />
        )}
        <Metric
          icon={DoorClosed}
          label="Chambres SIP"
          value={stats ? `${stats.rooms_online}/${stats.rooms}` : "—"}
          sub="enregistrées"
          ratio={stats ? { online: stats.rooms_online, total: stats.rooms } : null}
        />
        <Metric icon={Cable} label="Trunks 3CX" value={stats?.trunks ?? "—"} sub="configurés" />
        {!isSuper && stats?.scope === "hotel" && stats?.phones_bound > 0 && (
          <Metric
            icon={Phone}
            label="Postes provisionnés"
            value={`${stats.phones_bound}/${stats.rooms}`}
            sub="liés à une chambre"
            ratio={{ online: stats.phones_bound, total: stats.rooms }}
          />
        )}
      </div>

      <div className="mt-8 overflow-hidden rounded-sm border border-zinc-200 bg-white">
        <div className="flex items-center justify-between border-b border-zinc-200 px-5 py-3">
          <h2 className="font-head text-base font-bold tracking-tight text-zinc-900">
            Hôtels
          </h2>
          <button
            data-testid="goto-hotels-btn"
            onClick={() => navigate("/hotels")}
            className="font-mono text-xs text-zinc-400 hover:text-zinc-700 transition-colors"
          >
            Tout voir →
          </button>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-zinc-100 text-left text-[9px] uppercase tracking-[0.18em] text-zinc-400">
              <th className="px-5 py-2.5 font-bold">Hôtel</th>
              <th className="px-5 py-2.5 font-bold hidden md:table-cell">Domaine SIP</th>
              <th className="px-5 py-2.5 font-bold hidden lg:table-cell">Serveur</th>
              <th className="px-5 py-2.5 font-bold">Chambres</th>
              <th className="px-5 py-2.5 font-bold">Trunk</th>
              <th className="w-8"></th>
            </tr>
          </thead>
          <tbody>
            {hotels.map((h) => {
              const roomPct = h.rooms_count > 0 ? Math.round((h.rooms_online / h.rooms_count) * 100) : 0;
              const roomColor = roomPct >= 80 ? "bg-emerald-500" : roomPct >= 50 ? "bg-amber-400" : "bg-zinc-300";
              return (
                <tr
                  key={h.id}
                  data-testid={`dashboard-hotel-${h.id}`}
                  onClick={() => navigate(`/hotels/${h.id}`)}
                  className={`cursor-pointer border-b border-zinc-50 transition-colors last:border-0 hover:bg-zinc-50/70 ${
                    h.trunk_status === "error" ? "bg-red-50/30" : ""
                  }`}
                >
                  <td className="px-5 py-3">
                    <span className="font-medium text-zinc-900">{h.name}</span>
                  </td>
                  <td className="px-5 py-3 hidden md:table-cell">
                    <span className="font-mono text-[11px] text-zinc-500">{h.sip_domain}</span>
                  </td>
                  <td className="px-5 py-3 hidden lg:table-cell">
                    <span className="font-mono text-[11px] text-zinc-500">{h.server_name}</span>
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-xs text-zinc-700 tabular-nums">{h.rooms_online}/{h.rooms_count}</span>
                      <div className="w-12 h-[3px] rounded-full bg-zinc-100">
                        <div className={`h-full rounded-full ${roomColor}`} style={{ width: `${roomPct}%` }} />
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-3">
                    <StatusBadge status={h.trunk_status} />
                  </td>
                  <td className="pr-4 text-right">
                    <ArrowUpRight className="ml-auto h-3.5 w-3.5 text-zinc-300" />
                  </td>
                </tr>
              );
            })}
            {hotels.length === 0 && (
              <tr>
                <td colSpan={6} className="px-5 py-8 text-center text-sm text-zinc-400">
                  Aucun hôtel pour le moment.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
